// MusicDirector (§13.4, §25): picks the song for the room phase, locks round songs to the tempo
// clock, feeds the adaptive layers and the listener's mood, and owns everything musical that SFX
// ask for: the musical-bonk chain, quantized stingers, the teeter low-pass and its "slam back", the
// potato and ghost filters, the round countdown and the final-KO tape stop.
import { NO_SLOT } from '@shared/constants';
import { BS, MapId, ModeId, RoomPhase, RoundPhase } from '@shared/ids';
import { MAP_TEMPO } from '@shared/maps/clock';
import type { FrameView } from '../../game/FrameView';
import type { Mixer } from '../mixer';
import { compileSong, songDrums, songInstruments, type CompiledSong } from './arrangement';
import { FreeClock, TempoMap, ViewClock, type TickClock } from './clock';
import type { Sampler, SampledInstrument } from './sampler';
import { defaultMood, GHOST_DETUNE_CENTS, SongPlayer, type MusicMood } from './sequencer';
import type { DrumId } from './songTypes';
import { songForMap, SONG_IDS, SONGS, type SongId } from './songs/index';
import { STINGERS, type StingerEnv, type StingerId } from './stingers';
import { bonkPitch, chainIndex, fitToChord, isChromatic, isMinorScale, nearestWithPc, pc } from './theory';

/** Lookahead horizon of the scheduler, seconds (§13.4: ≤ 120 ms). */
export const LOOKAHEAD_S = 0.12;
/** §13.4 musical bonks: hits within 1.5 s chain. */
export const CHAIN_WINDOW_S = 1.5;
/** §13.4 teeter filter. */
const TEETER_STATES: readonly number[] = [BS.TEETER, BS.LEDGE];
/** Round ticks of the countdown cues (§5). */
export const COUNTDOWN_TICKS: readonly (readonly [number, 'three' | 'two' | 'one' | 'bonk'])[] = [[-180, 'three'], [-120, 'two'], [-60, 'one'], [0, 'bonk']];
const INTERMISSION_LOWPASS = 1100;
/** The same stinger asked for this close to its last start is the same moment (dropped). */
const STINGER_DEDUPE_S = 0.1;
/** Countdown cues commit this close to their tick (checked every frame and scheduler tick). */
const COUNTDOWN_COMMIT_S = 0.08;

export interface RoundInfo { mapId: MapId; modeId: ModeId; golden: boolean; roundIndex: number; startRoomTick: number }

export interface MusicHooks {
  /** Plays a one-shot SFX (announcer, siren, countdown beep, tape stop) at a context time. */
  sfx(key: string, when: number, opts?: { gain?: number; rate?: number }): void;
  caption(text: string, sec?: number): void;
  /** A tick-locked cue was scheduled (sync log). */
  synced(cue: string, tick: number, ctxTime: number): void;
}

interface Target { key: string; song: SongId; locked: boolean; intermission: boolean }

const FREE: Readonly<Record<SongId, Target>> = Object.fromEntries(SONG_IDS.map((id) => [id, { key: `free:${id}`, song: id, locked: false, intermission: false }])) as Record<SongId, Target>;
const INTERMISSION: Target = { key: 'free:intermission', song: 'lounge', locked: false, intermission: true };

const COMPILED = new Map<SongId, CompiledSong>();
export function compiled(id: SongId): CompiledSong {
  let c = COMPILED.get(id);
  if (!c) {
    c = compileSong(SONGS[id]);
    COMPILED.set(id, c);
  }
  return c;
}

/** Every sampled instrument and drum a song (plus stingers and bonks) needs. */
export function songRequirements(id: SongId): { instruments: SampledInstrument[]; drums: DrumId[] } {
  const def = SONGS[id];
  const inst = new Set<SampledInstrument>(songInstruments(def) as SampledInstrument[]);
  const drums = new Set<DrumId>(songDrums(def));
  inst.add('bonkTone');
  inst.add('sparkle');
  for (const s of Object.values(STINGERS)) {
    for (const i of s.instruments) inst.add(i);
    for (const d of s.drums) drums.add(d);
  }
  return { instruments: [...inst], drums: [...drums] };
}

function isScoring(modeId: ModeId): boolean {
  return modeId === ModeId.ROYALE || modeId === ModeId.HOT_POTATO || modeId === ModeId.CROWN_GRAB;
}

export class MusicDirector {
  readonly viewClock = new ViewClock();
  current: SongPlayer | null = null;
  private target: Target | null = null;
  private menu = false;
  private round: RoundInfo | null = null;
  private roundSerial = 0;
  /** The round's music has ended (final-KO tape stop or ROUND_OVER); `resumeAt` is when the
   * intermission groove may start. */
  private ended = false;
  private resumeAt = 0;
  private chainStep = -1;
  private chainAt = -Infinity;
  private replayStep = -1;
  private replayAt = -Infinity;
  private teeterDipped = false;
  private countdownDone = new Set<number>();
  private lastView: FrameView | null = null;
  private replayDuck: number | null = null;
  private roundOverHandled = false;
  private lockedSerial = Number.NaN;
  /** When each stinger last started: simultaneous KOs (a double bonk, the deciding tick) land on
   * the same grid point and must not stack the same phrase on itself. */
  private readonly stingerStarts = new Map<StingerId, number>();

  constructor(private readonly ctx: BaseAudioContext, private readonly mixer: Mixer, private readonly sampler: Sampler, private readonly hooks: MusicHooks) {}

  // ---- Engine inputs ----------------------------------------------------------------------------

  setMenu(on: boolean): void {
    this.menu = on;
  }

  setRound(info: RoundInfo): void {
    this.round = { ...info };
    this.roundSerial++;
    this.countdownDone = new Set();
    this.ended = false;
    this.roundOverHandled = false;
    this.viewClock.reset();
    this.mixer.musicRestore(this.ctx.currentTime);
    this.chainStep = -1;
  }

  /** Per frame (non-replay frames feed the clock). `ctxNow` is the context time heard at view.now. */
  update(view: FrameView, ctxNow: number): void {
    this.lastView = view;
    if (view.source !== 'replay') {
      const jumped = this.viewClock.observe(view.roundTick, ctxNow);
      if (jumped && this.current && this.target?.locked) this.resyncLocked();
    }
    const want = this.pickTarget(view);
    if (want && (!this.target || want.key !== this.target.key)) this.switchTo(want, view);
    if (this.current) this.applyMood(view, this.current.mood);
    this.teeter(view);
    this.moodFilter(view);
    this.countdown(view);
    this.mixer.update();
  }

  /** The 25 ms scheduler tick. */
  schedule(): void {
    this.current?.schedule(this.ctx.currentTime + LOOKAHEAD_S);
    if (this.lastView) this.countdown(this.lastView);
  }

  // ---- Song selection ---------------------------------------------------------------------------

  private pickTarget(view: FrameView): Target | null {
    if (this.menu) return FREE.lounge;
    const r = this.round;
    switch (view.roomPhase) {
      case RoomPhase.LOBBY:
        return FREE.lounge;
      case RoomPhase.PODIUM:
        return FREE.podium;
      case RoomPhase.INTERMISSION:
      case RoomPhase.AWARDS:
        return INTERMISSION;
      case RoomPhase.ROUND_END:
        return this.ended ? INTERMISSION : this.target;
      default: {
        const mapId = view.mapId;
        const modeId = view.modeId;
        if (!isScoring(modeId)) return FREE[songForMap(mapId)];
        if (this.ended) return INTERMISSION;
        const serial = r && r.mapId === mapId ? this.roundSerial : -1;
        const t = this.target;
        // A round's mode never changes mid-round; the key is its serial and map.
        if (t && t.locked && t.song === songForMap(mapId) && this.lockedSerial === serial) return t;
        this.lockedSerial = serial;
        return { key: `round:${serial}:${mapId}`, song: songForMap(mapId), locked: true, intermission: false };
      }
    }
  }

  private switchTo(want: Target, view: FrameView): void {
    const now = this.ctx.currentTime;
    const prev = this.current;
    // After the round's end the groove waits for the stop (and a breath) before its downbeat.
    const after = this.ended && !want.locked ? Math.max(now + 0.06, this.resumeAt) : now + 0.06;
    prev?.stop(now, want.locked ? 0.25 : 0.6);
    this.target = want;
    const song = compiled(want.song);
    if (this.ended && !want.locked) this.mixer.musicRestore(Math.max(now, after - 0.03));
    if (want.locked) {
      const { tpb, tpbSD } = MAP_TEMPO[view.mapId];
      const clk = { rampStart: view.clock.rampStart, sdStart: view.clock.sdStart, cap: view.clock.cap };
      const tempo = new TempoMap(tpb, tpbSD, clk.sdStart);
      const clock = this.viewClock;
      const startStep = view.roundTick < -16 ? -32 : Math.ceil(tempo.beatAtTick(Math.max(view.roundTick, clock.anchored ? clock.tickAt(now) : view.roundTick)) * 4);
      this.current = new SongPlayer(this.ctx, {
        song, tempo, clock, sampler: this.sampler, dest: this.mixer.music, startStep,
        phaseAt: (tick) => (tick < 0 ? RoundPhase.INTRO : tick >= clk.cap ? RoundPhase.OVER : tick >= clk.sdStart ? RoundPhase.SUDDEN_DEATH : tick >= clk.rampStart ? RoundPhase.RAMP : RoundPhase.CALM),
        mood: defaultMood(), onBarLine: () => clock.barLine(), seed: this.roundSerial + 1, introRiser: true,
      });
    } else {
      const tpb = MAP_TEMPO[want.song === 'podium' ? MapId.PODIUM : MapId.LOBBY].tpb;
      // Free songs keep the tempo of their map. They count in with the song's pickup bar (bar −1)
      // and land on a crash at bar 0; the intermission groove just starts, quietly.
      const pickup = want.intermission ? 0 : 16;
      const tempo = new TempoMap(tpb);
      const clock = new FreeClock(after + tempo.tickAtBeat(pickup / 4) / 60);
      this.current = new SongPlayer(this.ctx, {
        song, tempo, clock, sampler: this.sampler, dest: this.mixer.music, startStep: -pickup,
        phaseAt: () => RoundPhase.CALM, mood: defaultMood(), lowpass: want.intermission ? INTERMISSION_LOWPASS : undefined, seed: 7,
      });
      this.current.mood.full = !want.intermission;
      this.current.mood.intermission = want.intermission;
    }
    this.applyMood(view, this.current.mood);
  }

  private resyncLocked(): void {
    const p = this.current;
    if (!p) return;
    // Rebuild the player at the clock's new position (a seek or a hard resync).
    const view = this.lastView;
    this.target = null;
    if (view) {
      p.stop(this.ctx.currentTime, 0.08);
      this.current = null;
    }
  }

  private applyMood(view: FrameView, m: MusicMood): void {
    const me = view.localSlot !== NO_SLOT ? view.beans[view.localSlot] : undefined;
    const scoring = isScoring(view.modeId) && !m.full && !m.intermission;
    const r = this.round;
    const golden = scoring && (view.golden || (r?.golden ?? false));
    m.golden = golden;
    m.transpose = scoring ? Math.min(4, Math.max(0, r?.roundIndex ?? view.roundIndex)) + (golden ? 2 : 0) : 0;
    m.leader = scoring && (me?.crowned ?? false) && !(me?.ghost ?? false);
    m.potato = scoring && (me?.hasPotato ?? false);
    m.ghost = me?.ghost ?? false;
    m.matchPoint = scoring && view.mode.matchPoint;
    m.fewAlive = scoring && (view.modeId === ModeId.ROYALE || view.modeId === ModeId.HOT_POTATO)
      && view.roomPhase === RoomPhase.PLAY && view.roundTick > 0 && view.mode.alive > 0 && view.mode.alive <= 2;
  }

  // ---- Filters ----------------------------------------------------------------------------------

  private teeter(view: FrameView): void {
    const me = view.localSlot !== NO_SLOT ? view.beans[view.localSlot] : undefined;
    const onEdge = !!me && me.active && TEETER_STATES.includes(me.state) && view.source !== 'replay';
    if (onEdge && !this.teeterDipped) {
      this.teeterDipped = true;
      this.mixer.teeterDip();
    } else if (!onEdge && this.teeterDipped) {
      this.teeterDipped = false;
      const fell = !me || me.state === BS.FALLING || me.state === BS.GHOST || me.out;
      if (fell) {
        this.mixer.teeterRelease(0.4);
      } else {
        // §13.4: the save snaps the filter back over 60 ms and the beat slams back on the next 16th.
        this.mixer.teeterRelease(0.06);
        this.current?.accent(this.ctx.currentTime);
      }
    }
  }

  get teeterActive(): boolean {
    return this.teeterDipped;
  }

  private moodFilter(view: FrameView): void {
    let hz = 20000;
    if (view.modeId === ModeId.HOT_POTATO && view.mode.potatoSlot !== NO_SLOT && view.roomPhase === RoomPhase.PLAY && !this.current?.mood.full) {
      const x = Math.max(0, Math.min(1, view.mode.potatoIntensity / 15));
      hz = 600 + 14000 * x * x;
    }
    const me = view.localSlot !== NO_SLOT ? view.beans[view.localSlot] : undefined;
    if (me?.ghost) hz = Math.min(hz, 4000);
    this.mixer.setMoodCutoff(hz);
  }

  // ---- Countdown (§5: "3", "2", "1" at −180/−120/−60, "BONK!" at 0) -------------------------------

  private countdown(view: FrameView): void {
    // Wait for a settled clock: a round's first frames show the init tick until the first snapshot
    // corrects it. A cue the correction has already passed plays at once as a catch-up.
    if (!this.target?.locked || !this.viewClock.stable || view.source === 'replay') return;
    const now = this.ctx.currentTime;
    for (const [tick, word] of COUNTDOWN_TICKS) {
      if (this.countdownDone.has(tick)) continue;
      if (this.viewClock.preciseTime(tick) > now + COUNTDOWN_COMMIT_S || !this.viewClock.due(tick, COUNTDOWN_COMMIT_S)) continue;
      const at = this.viewClock.cueTime(tick);
      this.countdownDone.add(tick);
      if (at < now - 0.5) continue;
      this.playCountdown(word, tick, Math.max(now, at), at < now - 0.01);
    }
  }

  /** A COUNTDOWN event (b = 3|2|1|0) the clock has not already scheduled. */
  countdownEvent(b: number, tick: number): void {
    const cue = COUNTDOWN_TICKS[3 - Math.max(0, Math.min(3, b))];
    if (this.countdownDone.has(cue[0])) return;
    this.countdownDone.add(cue[0]);
    const now = this.ctx.currentTime;
    const at = this.viewClock.anchored && this.target?.locked ? Math.max(now, this.viewClock.preciseTime(tick)) : now;
    this.playCountdown(cue[1], cue[0], at);
  }

  private playCountdown(word: 'three' | 'two' | 'one' | 'bonk', tick: number, at: number, late = false): void {
    if (word === 'bonk') {
      this.stingerAt(this.round?.golden ? 'goldenFanfare' : 'roundStart', at);
    } else {
      this.hooks.sfx(`ann.${word}`, at);
      // The beep (rendered at E5) sits on the key's 5th, so it never fights the intro bars.
      const fifth = nearestWithPc(76, pc(this.tonicAt(at) + 7));
      this.hooks.sfx('countdownBeep', at, { rate: 2 ** ((fifth - 76) / 12) });
      this.hooks.caption(word === 'three' ? '3…' : word === 'two' ? '2…' : '1…', 0.9);
    }
    // Late catch-ups are not tick-locked cues.
    if (!late) this.hooks.synced(`countdown:${word}`, tick, at);
  }

  // ---- Musical bonks and stingers ---------------------------------------------------------------

  /** Key root (MIDI) sounding at `t`, F (the lounge) when silent. */
  tonicAt(t: number): number {
    return this.current && !this.current.isStopped ? this.current.harmonyAt(t).tonic : 65;
  }

  /**
   * The next 16th (unit 1) or 8th (unit 2) at or after now (or after `from`, for a cue that
   * already booked the previous grid point); now when no song plays.
   */
  quantize(unitSteps: number, from = 0): number {
    const now = this.ctx.currentTime;
    if (!this.current || this.current.isStopped) return Math.max(now, from);
    const t = this.current.nextGrid(Math.max(now, from), unitSteps);
    return Number.isFinite(t) && t - now < 0.6 ? t : Math.max(now, from);
  }

  /**
   * The next backbeat (beat 2 or 4) of a locked round song at or after `from`, or NaN when no
   * locked song plays (the crowd only claps along with a round's score).
   */
  nextBackbeat(from: number): number {
    const p = this.current;
    if (!p || p.isStopped || !this.target?.locked) return Number.NaN;
    return p.nextGrid(from, 8, 0.004, 4);
  }

  /**
   * §13.4: steps the chain on a HIT and returns where and when its tonal part sounds. Replayed
   * hits climb their own chain, so a replay never disturbs the live round's phrase.
   */
  bonk(replay = false): { midi: number; when: number; step: number } {
    const now = this.ctx.currentTime;
    let step: number;
    if (replay) {
      this.replayStep = now - this.replayAt <= CHAIN_WINDOW_S ? this.replayStep + 1 : 0;
      this.replayAt = now;
      step = this.replayStep;
    } else {
      this.chainStep = now - this.chainAt <= CHAIN_WINDOW_S ? this.chainStep + 1 : 0;
      this.chainAt = now;
      step = this.chainStep;
    }
    const when = this.quantize(1);
    const p = this.current && !this.current.isStopped ? this.current : null;
    const scale = p ? p.song.def.scale : 'major';
    const tonic = this.tonicAt(when);
    let midi = bonkPitch(tonic, step, isMinorScale(scale));
    // On a chromatic chord (♭II7, iv7, a secondary dominant) the key's pentatonic can sit a minor
    // 9th against a chord tone: the note moves to the chord, keeping the phrase's direction.
    // Diatonic bars keep the spec's pentatonic steps exactly.
    const chord = p?.harmonyAt(when).bar.chord;
    if (chord && isChromatic(chord, scale)) midi = fitToChord(midi, tonic, chord, chainIndex(step) >= chainIndex(step - 1) ? 1 : -1);
    return { midi, when, step };
  }

  resetChain(replay = false): void {
    if (replay) {
      this.replayStep = -1;
      this.replayAt = -Infinity;
    } else {
      this.chainStep = -1;
      this.chainAt = -Infinity;
    }
  }

  get chain(): number {
    return this.chainStep;
  }

  /** A KO resolves the phrase: ta-da on the root at the next 8th, and the chain resets. */
  koResolve(replay = false): number {
    this.resetChain(replay);
    return this.stinger('koTada');
  }

  /** Plays a stinger at the next 8th (default) and returns its start time. */
  stinger(id: StingerId, unitSteps = 2): number {
    const at = this.quantize(unitSteps);
    this.stingerAt(id, at);
    return at;
  }

  /** Plays a stinger at `at`, in the key sounding at `keyAt` (default `at`). */
  stingerAt(id: StingerId, at: number, keyAt = at): void {
    const prev = this.stingerStarts.get(id);
    if (prev !== undefined && Math.abs(at - prev) < STINGER_DEDUPE_S) return;
    this.stingerStarts.set(id, at);
    const def = STINGERS[id];
    const p = this.current && !this.current.isStopped ? this.current : null;
    const tonic = this.tonicAt(keyAt);
    const beat = p ? p.tempo.beatSec(p.clock.tickAt(keyAt)) : 60 / 112.5;
    const scale = p?.song.def.scale ?? 'major';
    const env: StingerEnv = {
      sampler: this.sampler, dest: this.mixer.stingers, at, tonic, beat,
      minor: isMinorScale(scale),
      detune: p?.mood.ghost ? GHOST_DETUNE_CENTS : 0,
      sfx: (key, when) => this.hooks.sfx(key, when),
    };
    def.play(env);
    this.hooks.caption(def.caption, 1.6);
  }

  // ---- Round end, replays -----------------------------------------------------------------------

  /** §13.4 final KO of the round: the music tapes to a stop over 300 ms. */
  tapeStop(): void {
    if (this.ended || !this.current || this.current.isStopped || !this.target?.locked) return;
    const now = this.ctx.currentTime;
    this.mixer.musicFade(0, 0.3, now);
    this.hooks.sfx('tapeStop', now);
    this.current.stop(now + 0.3, 0.02);
    this.ended = true;
    this.resumeAt = now + 1.2;
  }

  /** ROUND_OVER without a final KO: a crash on the next downbeat and a 1 s fade. */
  roundOver(): void {
    if (this.roundOverHandled) return;
    this.roundOverHandled = true;
    if (this.ended || !this.current || this.current.isStopped || !this.target?.locked) return;
    const at = this.quantize(16);
    this.current.accent(at - 0.01);
    this.current.stop(at, 1.2);
    this.ended = true;
    this.resumeAt = at + 1.4;
  }

  replayStart(kind: 'instant' | 'bonkcam' | 'potm'): void {
    const db = kind === 'bonkcam' ? -8 : -6;
    if (this.replayDuck !== null) this.mixer.unduck(this.replayDuck);
    this.replayDuck = db;
    this.mixer.duck(db, Infinity);
  }

  replayEnd(): void {
    if (this.replayDuck !== null) this.mixer.unduck(this.replayDuck);
    this.replayDuck = null;
  }

  /** Diagnostics for the soundboard and tests. */
  get state(): { song: string; locked: boolean; step: number; tonic: number; drift: number; reanchors: number; chain: number } {
    const now = this.ctx.currentTime;
    const p = this.current;
    return {
      song: this.target?.key ?? 'none', locked: this.target?.locked ?? false,
      step: p ? p.stepAt(now) : 0, tonic: this.tonicAt(now), drift: this.viewClock.drift, reanchors: this.viewClock.reanchors, chain: this.chainStep,
    };
  }
}

/** Every song id (pre-render order hint). */
export const ALL_SONGS: readonly SongId[] = SONG_IDS;
export type { TickClock };
